/* eslint-disable unicorn/filename-case */
import { createFileRoute } from '@tanstack/react-router';

import { TabOverview } from '../-tab-overview';
import type { GetSeoStrategyByBrandIdRes } from '../../../../entity/api/routes/seo-strategy';
import type { SeoStrategyKeywordFunnel } from '../../../../entity/api/seo-strategy-keywords';
import { addTrackCrumb } from '../../../../error/tracking';
import { useSeoStrategyByBrandId } from '../../../../services/storage/seo-strategy-queries';
import { tNamespace } from '../../../../services/translation';

const t = tNamespace('app.seo-strategy-overview');

export const Route = createFileRoute('/app/_sidebar-layout/seo-strategy/$brandId/overview')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/_sidebar-layout/seo-strategy/$brandId/overview',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: SeoStrategyOverviewTab,
});

type RankingBucket = {
  label: string;
  count: number;
  percentage: number;
  widthPercent: number;
};

type ConversionProbabilityItem = {
  level: SeoStrategyKeywordFunnel;
  description: string;
  keywordCount: number;
  searchVolume: number;
};

type SwotItem = {
  text: string;
};

export type OverviewData = {
  totalKeywords: number;
  monthlySearchVolume: number;
  currentlyRanking: number;
  currentlyRankingPercent: number;
  notRanking: number;
  notRankingPercent: number;
  rankingBuckets: RankingBucket[];
  conversionProbability: ConversionProbabilityItem[];
  strategicAnalysis: string;
  strengths: SwotItem[];
  opportunities: SwotItem[];
  weaknesses: SwotItem[];
};

function toPercent(count: number, total: number): number {
  if (total === 0) {
    return 0;
  }
  return Math.round((count / total) * 1000) / 10;
}

type SeoData = GetSeoStrategyByBrandIdRes['data'];

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
function SeoStrategyOverviewTab() {
  const { brandId } = Route.useParams();
  const { data: seoStrategy } = useSeoStrategyByBrandId(brandId);

  const typedData: SeoData | undefined = seoStrategy;
  const strategy = typedData?.strategy;
  const stats = typedData?.stats;
  const keywordsTotal = typedData?.keywordsTotal;

  const distribution = stats?.distribution;
  const notRankingCount = distribution?.notRanking ?? 0;
  const currentlyRankingCount = (keywordsTotal ?? 0) - notRankingCount;

  const rankingBuckets: RankingBucket[] = [
    {
      label: 'Top 3',
      count: distribution?.top3 ?? 0,
      percentage: toPercent(distribution?.top3 ?? 0, keywordsTotal ?? 0),
      widthPercent: toPercent(distribution?.top3 ?? 0, keywordsTotal ?? 0),
    },
    {
      label: 'Top 10',
      count: distribution?.top10 ?? 0,
      percentage: toPercent(distribution?.top10 ?? 0, keywordsTotal ?? 0),
      widthPercent: toPercent(distribution?.top10 ?? 0, keywordsTotal ?? 0),
    },
    {
      label: 'Top 30',
      count: distribution?.top30 ?? 0,
      percentage: toPercent(distribution?.top30 ?? 0, keywordsTotal ?? 0),
      widthPercent: toPercent(distribution?.top30 ?? 0, keywordsTotal ?? 0),
    },
    {
      label: t('beyond30'),
      count: distribution?.beyond30 ?? 0,
      percentage: toPercent(distribution?.beyond30 ?? 0, keywordsTotal ?? 0),
      widthPercent: toPercent(distribution?.beyond30 ?? 0, keywordsTotal ?? 0),
    },
    {
      label: t('notRankingLabel'),
      count: distribution?.notRanking ?? 0,
      percentage: toPercent(distribution?.notRanking ?? 0, keywordsTotal ?? 0),
      widthPercent: toPercent(distribution?.notRanking ?? 0, keywordsTotal ?? 0),
    },
  ];

  const conversionProbability: ConversionProbabilityItem[] = (stats?.volumeByFunnel ?? []).map(
    function ({ funnel, volume, count }) {
      const percent = toPercent(volume, stats?.totalVolume ?? 0);
      return {
        level: funnel as SeoStrategyKeywordFunnel,
        description: t(count !== 1 ? 'volumeDescriptionPlural' : 'volumeDescription', {
          percent,
          count,
        }),
        keywordCount: count,
        searchVolume: volume,
      };
    }
  );

  const overviewData: OverviewData = {
    totalKeywords: keywordsTotal ?? 0,
    monthlySearchVolume: stats?.totalVolume ?? 0,
    currentlyRanking: currentlyRankingCount ?? 0,
    currentlyRankingPercent: toPercent(currentlyRankingCount, keywordsTotal ?? 0),
    notRanking: notRankingCount,
    notRankingPercent: toPercent(notRankingCount, keywordsTotal ?? 0),
    rankingBuckets,
    conversionProbability,
    strategicAnalysis: strategy?.sumOverview ?? '',
    strengths:
      strategy?.sumStrengths.map(function (text) {
        return { text };
      }) ?? [],
    opportunities:
      strategy?.sumOpportunities.map(function (text) {
        return { text };
      }) ?? [],
    weaknesses:
      strategy?.sumWeaknesses.map(function (text) {
        return { text };
      }) ?? [],
  };

  return (
    <div className="mx-auto w-full max-w-[800px] pb-12 pt-0 md:py-6">
      <TabOverview data={overviewData} brandId={brandId} />
    </div>
  );
}
